import { Input } from '@affine/component';
import type { PageInfoCustomPropertyMeta } from '@affine/core/modules/properties/services/schema';
import { useI18n } from '@affine/i18n';
import type { KeyboardEventHandler } from 'react';
import { useCallback } from 'react';

import { IconsSelectorButton } from './icons-selector';
import * as styles from './styles.css';
import {
  DocPropertyTypes,
  isSupportedDocPropertyType,
} from './types/constants';
import {
  getDefaultIconName,
  getSafeIconName,
  nameToIcon,
  type PagePropertyIcon,
} from './types/icons-mapping';

export const EditPropertyNameMenuItem = ({
  property,
  onNameBlur: onBlur,
  onNameChange,
  onIconChange,
}: {
  onNameBlur: (e: string) => void;
  onNameChange: (e: string) => void;
  onIconChange: (icon: PagePropertyIcon) => void;
  property: PageInfoCustomPropertyMeta;
}) => {
  const typeInfo =
    property.type && isSupportedDocPropertyType(property.type)
      ? DocPropertyTypes[property.type]
      : undefined;

  const iconName = getSafeIconName(property.icon, property.type);
  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    e => {
      if (e.key !== 'Escape') {
        e.stopPropagation();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        onBlur(e.currentTarget.value);
      }
    },
    [onBlur]
  );
  const handleBlur = useCallback(
    (e: FocusEvent & { currentTarget: HTMLInputElement }) => {
      onBlur(e.currentTarget.value);
    },
    [onBlur]
  );

  const t = useI18n();
  return (
    <div className={styles.propertyRowNamePopupRow}>
      <IconsSelectorButton
        selected={iconName}
        onSelectedChange={onIconChange}
      />
      {typeInfo?.renameable || property.readonly ? (
        <span className={styles.propertyName}>{property.name}</span>
      ) : (
        <Input
          defaultValue={property.name}
          onBlur={handleBlur}
          onChange={onNameChange}
          placeholder={t['unnamed']()}
          onKeyDown={onKeyDown}
        />
      )}
    </div>
  );
};

export const PropertyTypeMenuItem = ({
  property,
}: {
  property: PageInfoCustomPropertyMeta;
}) => {
  const Icon = nameToIcon(getDefaultIconName(property.type), property.type);
  const t = useI18n();
  return (
    <div className={styles.propertyRowTypeItem}>
      {t['com.affine.doc-properties.create-property.menu.header']()}
      <div className={styles.propertyTypeName}>
        <Icon />
        {t[`com.affine.doc-properties.property.${property.type}`]()}
      </div>
    </div>
  );
};
